/**
 * The deployment-to-theme and deployment-to-browser identity bridge. The
 * saved platform brand is one query used by both token hydration and the
 * browser tab, so saving settings updates the live host without a reload.
 */
import { useEffect, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useDocumentIdentity, useTheme } from '@solum/ui'
import { useSolum } from '../data/client'

interface SavedBrand {
  name?: string
  patch?: Record<string, unknown> & { browser_title?: string; favicon?: string }
}

export function BrandSync() {
  const { isLive, client } = useSolum()
  const { override, brands, setBrandIndex, hydrateBrand } = useTheme()
  const appliedTheme = useRef<string | null>(null)
  const saved = useQuery({
    queryKey: ['setting', 'platform', 'brand'],
    queryFn: () => client.getSetting<SavedBrand | null>('platform', 'brand'),
    enabled: isLive,
  })

  useDocumentIdentity({
    title: saved.data?.patch?.browser_title,
    /* The deployment's configured name, else the name an applied deployment
       brand carries, else the neutral platform name. Never the shipped base
       brand's: an unconfigured or not-yet-read deployment is not Moonbase
       (MB-Opera #91 G6). A Moonbase estate still says Moonbase, from its data. */
    fallbackTitle: saved.data?.name ?? override?.name ?? 'Marble',
    favicon: saved.data?.patch?.favicon,
  })

  useEffect(() => {
    const value = saved.data
    if (!isLive || !value || typeof value !== 'object') return
    const signature = JSON.stringify(value)
    if (appliedTheme.current === signature) return
    appliedTheme.current = signature
    if (value.name) {
      const index = brands.findIndex((candidate) => candidate.name === value.name)
      if (index >= 0) setBrandIndex(index)
    }
    if (value.patch && typeof value.patch === 'object') {
      hydrateBrand(value.patch as Parameters<typeof hydrateBrand>[0])
    }
  }, [isLive, saved.data, brands, setBrandIndex, hydrateBrand])

  return null
}
